import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api.js";
import { formatDate, ratio } from "../ui.js";
import type { GalleryItem } from "../types.js";

const PAGE_SIZE = 20;

function paginationPages(current: number, total: number) {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  const pages: Array<number | "ellipsis"> = [1];
  const firstMiddle = Math.max(2, current - 1);
  const lastMiddle = Math.min(total - 1, current + 1);
  if (firstMiddle > 2) pages.push("ellipsis");
  for (let page = firstMiddle; page <= lastMiddle; page += 1) pages.push(page);
  if (lastMiddle < total - 1) pages.push("ellipsis");
  pages.push(total);
  return pages;
}

export function GalleryScreen() {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [templates, setTemplates] = useState<string[]>([]);
  const [clientId, setClientId] = useState("all");
  const [templateName, setTemplateName] = useState("all");
  const [period, setPeriod] = useState("30");
  const [format, setFormat] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
      period,
      format,
    });
    if (clientId !== "all") query.set("clientId", clientId);
    if (templateName !== "all") query.set("templateName", templateName);
    void api.listGallery(query).then(
      (result) => {
        if (cancelled) return;
        setItems(result.items);
        setTotal(result.total);
        setClients(result.facets?.clients ?? []);
        setTemplates(result.facets?.templates ?? []);
        setError("");
        setLoading(false);
      },
      () => {
        if (cancelled) return;
        setError("Не удалось загрузить галерею.");
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [clientId, format, page, period, templateName]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const pages = useMemo(
    () => paginationPages(page, totalPages),
    [page, totalPages],
  );
  const firstItem = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastItem = total ? firstItem + items.length - 1 : 0;

  return (
    <section className="gallery-page">
      <div className="page-heading">
        <div>
          <h1>Галерея</h1>
          <p>Все созданные изображения</p>
        </div>
      </div>
      <div className="panel gallery-filters">
        <label>
          Клиент
          <select
            aria-label="Клиент галереи"
            value={clientId}
            onChange={(event) => {
              setClientId(event.target.value);
              setPage(1);
            }}
          >
            <option value="all">Все клиенты</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Шаблон
          <select
            aria-label="Шаблон галереи"
            value={templateName}
            onChange={(event) => {
              setTemplateName(event.target.value);
              setPage(1);
            }}
          >
            <option value="all">Все шаблоны</option>
            {templates.map((name) => (
              <option key={name}>{name}</option>
            ))}
          </select>
        </label>
        <label>
          Период
          <select
            aria-label="Период галереи"
            value={period}
            onChange={(event) => {
              setPeriod(event.target.value);
              setPage(1);
            }}
          >
            <option value="7">За 7 дней</option>
            <option value="30">За 30 дней</option>
            <option value="90">За 90 дней</option>
            <option value="all">За всё время</option>
          </select>
        </label>
        <label>
          Формат
          <select
            aria-label="Формат галереи"
            value={format}
            onChange={(event) => {
              setFormat(event.target.value);
              setPage(1);
            }}
          >
            <option value="all">Все форматы</option>
            <option value="png">PNG</option>
            <option value="jpeg">JPEG</option>
            <option value="webp">WebP</option>
          </select>
        </label>
      </div>
      {error ? <div className="form-error">{error}</div> : null}
      {loading ? (
        <div className="gallery-loading">Загрузка изображений…</div>
      ) : null}
      <div className="gallery-grid polished-gallery" aria-busy={loading}>
        {items.map((item) => (
          <article key={item.id}>
            <Link className="gallery-image" to={`/generations/${item.batchId}`}>
              <img
                loading="lazy"
                src={`/api/v1/generation-items/${item.itemId}/thumbnail`}
                alt={item.templateName}
              />
            </Link>
            <div className="gallery-card-body">
              <strong>{item.clientName}</strong>
              <span>{item.templateName}</span>
              <small>
                {item.width} × {item.height} · {ratio(item.width, item.height)}{" "}
                · {item.mimeType.replace("image/", "").toUpperCase()}
              </small>
              <small>{formatDate(item.createdAt)}</small>
              <div className="gallery-actions">
                <Link to={`/generations/${item.batchId}`}>Открыть</Link>
                <a
                  className="button primary"
                  href={`/api/v1/generation-items/${item.itemId}/download-original`}
                >
                  ↓ Скачать
                </a>
                <a
                  className="button gallery-webp"
                  href={`/api/v1/generation-items/${item.itemId}/download-webp`}
                  title="WebP из оригинала, без изменения размеров и новой генерации"
                >
                  Сохранить в WebP
                </a>
              </div>
            </div>
          </article>
        ))}
      </div>
      {!items.length && !error && !loading ? (
        <div className="empty-state">
          По выбранным фильтрам изображений нет.
        </div>
      ) : null}
      {!error && totalPages > 0 ? (
        <nav className="gallery-pagination" aria-label="Страницы галереи">
          <span>
            Показано {firstItem}–{lastItem} из {total}
          </span>
          <div>
            <button
              type="button"
              aria-label="Предыдущая страница"
              disabled={loading || page === 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              ‹
            </button>
            {pages.map((value, index) =>
              value === "ellipsis" ? (
                <span key={`ellipsis-${index}`} aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  type="button"
                  key={value}
                  className={value === page ? "active" : undefined}
                  aria-label={`Страница ${value}`}
                  aria-current={value === page ? "page" : undefined}
                  disabled={loading}
                  onClick={() => setPage(value)}
                >
                  {value}
                </button>
              ),
            )}
            <button
              type="button"
              aria-label="Следующая страница"
              disabled={loading || page === totalPages}
              onClick={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
            >
              ›
            </button>
          </div>
        </nav>
      ) : null}
    </section>
  );
}
